const STORAGE_KEY = 'altior:exit-intent-suppressed-until';
const DAY_MS = 86_400_000;
const DESKTOP_DWELL_MS = 8_000;
const TOUCH_DWELL_MS = 20_000;

function suppressedUntil() {
  try {
    return Number(localStorage.getItem(STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** @param {number} days */
function suppressFor(days) {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now() + days * DAY_MS));
  } catch {}
}

class ExitIntentDialog extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLFormElement | null} */
  #form = null;
  #isTouch = window.matchMedia('(pointer: coarse)').matches;
  #armedAt = 0;
  #triggered = false;
  #subscribed = false;
  #sampleY = 0;
  #sampleAt = 0;
  #deepestY = 0;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#form = this.querySelector('form:not([method="dialog"])');
    if (!this.#dialog || !this.#form || Date.now() < suppressedUntil()) return;

    this.#armedAt = Date.now() + (this.#isTouch ? TOUCH_DWELL_MS : DESKTOP_DWELL_MS);

    if (this.#isTouch) {
      window.addEventListener('scroll', this.#onScroll, { passive: true });
    } else {
      document.documentElement.addEventListener('mouseout', this.#onMouseOut);
    }

    this.#dialog.addEventListener('click', this.#onDialogClick);
    this.#dialog.addEventListener('close', this.#onClose);
    this.#form.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#disarm();
    this.#dialog?.removeEventListener('click', this.#onDialogClick);
    this.#dialog?.removeEventListener('close', this.#onClose);
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  #disarm() {
    window.removeEventListener('scroll', this.#onScroll);
    document.documentElement.removeEventListener('mouseout', this.#onMouseOut);
  }

  /** @param {MouseEvent} event */
  #onMouseOut = (event) => {
    if (event.relatedTarget === null && event.clientY <= 0) this.#trigger();
  };

  // Touch has no pointer leaving the window; a fast flick back toward the
  // address bar after reading a good way down is the closest equivalent.
  #onScroll = () => {
    const y = window.scrollY;
    const now = performance.now();
    this.#deepestY = Math.max(this.#deepestY, y);

    const elapsed = now - this.#sampleAt;
    if (elapsed < 200) return;

    const rose = this.#sampleY - y;
    if (elapsed <= 450 && rose >= window.innerHeight * 0.3 && this.#deepestY >= window.innerHeight) {
      this.#trigger();
    }

    this.#sampleY = y;
    this.#sampleAt = now;
  };

  async #trigger() {
    if (this.#triggered || Date.now() < this.#armedAt || document.querySelector('dialog[open]')) return;
    this.#triggered = true;
    this.#disarm();

    try {
      const cartUrl = `${window.Theme?.routes?.cart_url ?? '/cart'}.js`;
      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (cart.item_count > 0) return;
    } catch {
      return;
    }

    if (document.querySelector('dialog[open]')) return;
    this.#dialog?.showModal();
    // Start focus on the dialog, not the close button, so opening doesn't draw a focus ring on dismiss.
    this.#dialog?.focus();
  }

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    if (event.target === this.#dialog) this.#dialog.close();
  };

  #onClose = () => {
    if (!this.#subscribed) suppressFor(Number(this.dataset.cooldownDays) || 14);
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (this.#form);
    const button = form.querySelector('button[type="submit"]');
    const error = this.querySelector('[data-exit-intent-error]');
    const success = this.querySelector('[data-exit-intent-success]');

    if (error instanceof HTMLElement) error.hidden = true;
    if (button instanceof HTMLButtonElement) button.disabled = true;

    try {
      const response = await fetch(form.action, { method: 'POST', body: new FormData(form) });
      const landedOn = new URL(response.url);

      // Shopify can interpose a bot challenge; let the browser handle it natively.
      if (landedOn.pathname.includes('/challenge')) {
        form.submit();
        return;
      }

      if (landedOn.searchParams.get('customer_posted') !== 'true') throw new Error('Signup not accepted');

      this.#subscribed = true;
      suppressFor(365);
      form.hidden = true;
      if (success instanceof HTMLElement) {
        success.hidden = false;
        success.focus();
        window.AltiorMotion?.playTick(success.querySelector('[data-exit-intent-tick]'));
      }
    } catch {
      if (error instanceof HTMLElement) error.hidden = false;
      window.AltiorMotion?.shake(form.querySelector('input[type="email"]'));
    } finally {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  };
}

if (!customElements.get('exit-intent-dialog')) {
  customElements.define('exit-intent-dialog', ExitIntentDialog);
}
